import React from 'react';
import dynamic from 'next/dynamic';
import Types from './component-types';

// Models
import AirTableFormModel from '../models/air-table-form';
import BikeCtaBreakerModel from '../models/bike-cta-breaker';
import CarouselModel from '../models/carousel';
import CtaBreakerModel from '../models/cta-breaker';
import FAQModel from '../models/faq';
import FeaturedArticlesModel from '../models/featured-articles';
import FeaturesComponentModel from '../models/features';
import FooterComponentModel from '../models/footer';
import HeroModel from '../models/hero';
import LegalComponentModel from '../models/legal';
import LocationsMapModel from '../models/locations-map';
import MediaComponent from '../models/media-component';
import NavbarModel from '../models/navbar';
import OfferingsModel from '../models/offerings';
import OpenSourceCreditEntriesModel from '../models/open-source-credit-entries';
import PdfDisplayModel from '../models/pdf-display';
import PDPWellModel from '../models/pdp-well';
import RedirectComponentModel from '../models/redirect-component';
import ReviewsModel from '../models/reviews';
import RichtextModel from '../models/richtext';
import SalesforceChatModel from '../models/salesforce-chat';
import SectionModel from '../models/section';
import SpecificationsModel from '../models/specifications';
import TestimonialsModel from '../models/testimonials';
import TitleBannerModel from '../models/title-banner';
import TwoUpModel from '../models/two-up';
import VerticalSpacerModel from '../models/vertical-spacer';
import VideoTilesModel from '../models/video-tiles';

// Components
const AirTableForm = dynamic(
  import('../components/air-table-form/AirTableForm')
);
const BikeCtaBreaker = dynamic(
  import('../components/bike-cta-breaker/BikeCtaBreaker')
);
const Carousel = dynamic(import('../components/carousel/Carousel'));
const CtaBreaker = dynamic(import('../components/cta-breaker/CtaBreaker'));
const FAQ = dynamic(import('../components/faq/Faq'));
const Features = dynamic(import('../components/features/Features'));
const Footer = dynamic(import('../components/footer/Footer'));
const Hero = dynamic(import('../components/hero/hero'));
const Legal = dynamic(import('../components/legal/LegalLayout'));
const LocationsMap = dynamic(
  import('../components/locations-map/LocationsMap')
);
const FullScreenMedia = dynamic(
  import('../components/full-screen-media/FullScreenMedia')
);
const Navbar = dynamic(import('../components/global/navbar/Navbar'));
const Offerings = dynamic(import('../components/offerings/Offerings'));
const OpenSourceCreditEntries = dynamic(
  import('../components/open-source-credit-entries/OpenSourceCreditEntries')
);
const PDPWell = dynamic(import('../components/pdp-well/pdp-well'));
const RedirectComponent = dynamic(
  import('../components/redirect-component/RedirectComponent')
);
const Reviews = dynamic(import('../components/reviews/Reviews'));
const Richtext = dynamic(import('../components/global/richtext/Richtext'));
const SalesforceChat = dynamic(
  () => import('../components/global/salesforce/Chat'),
  { ssr: false }
);
const Section = dynamic(import('../components/global/section/Section'));
const Specifications = dynamic(
  import('../components/specifications/Specifications')
);
const Testimonials = dynamic(import('../components/testimonials/Testimonials'));
const TitleBanner = dynamic(import('../components/title-banner/TitleBanner'));
const TwoUp = dynamic(import('../components/two-up/TwoUp'));
const VerticalSpacer = dynamic(
  import('../components/global/vertical-spacer/VerticalSpacer')
);
const FeaturedArticles = dynamic(
  import('../components/featured-articles/FeaturedArticles')
);
const VideoTiles = dynamic(import('../components/video-tiles/VideoTiles'));
const PdfDisplay = dynamic(import('../components/pdf-display/PdfDisplay'));

const getComponent = (component: any, index: number) => {
  if (!component || !component.sysInfo || !component.sysInfo.type) return <></>;

  const newComponentKey = `${component.sysInfo.type}-${index}`;

  switch (component.sysInfo.type) {
    case Types.AirTableForm:
      return (
        <AirTableForm
          key={newComponentKey}
          data={new AirTableFormModel(component)}
        />
      );
    case Types.Carousel:
      return (
        <Carousel key={newComponentKey} data={new CarouselModel(component)} />
      );
    case Types.BikeCtaBreaker:
      return (
        <BikeCtaBreaker
          key={newComponentKey}
          data={new BikeCtaBreakerModel(component)}
        />
      );
    case Types.CtaBreaker:
      return (
        <CtaBreaker
          key={newComponentKey}
          data={new CtaBreakerModel(component)}
        />
      );
    case Types.FAQComponent:
      return <FAQ key={newComponentKey} data={new FAQModel(component)} />;
    case Types.FeaturedArticles:
      return (
        <FeaturedArticles
          key={newComponentKey}
          data={new FeaturedArticlesModel(component)}
        />
      );
    case Types.FeaturesComponent:
      return (
        <Features
          key={newComponentKey}
          data={new FeaturesComponentModel(component)}
        />
      );
    case Types.FooterComponent:
      return (
        <Footer
          key={newComponentKey}
          data={new FooterComponentModel(component)}
        />
      );
    case Types.Hero:
      return <Hero key={newComponentKey} {...new HeroModel(component)} />;
    case Types.LegalComponent:
      return (
        <Legal
          key={newComponentKey}
          data={new LegalComponentModel(component)}
        />
      );
    case Types.LocationsMap:
      return (
        <LocationsMap
          key={newComponentKey}
          data={new LocationsMapModel(component)}
        />
      );
    case Types.Media:
      return (
        <FullScreenMedia
          key={newComponentKey}
          data={new MediaComponent(component)}
        />
      );
    case Types.Navbar:
      return <Navbar key={newComponentKey} data={new NavbarModel(component)} />;
    case Types.PDPWell:
      return (
        <PDPWell key={newComponentKey} data={new PDPWellModel(component)} />
      );
    case Types.Offerings:
      return (
        <Offerings key={newComponentKey} data={new OfferingsModel(component)} />
      );
    case Types.OpenSourceCreditsEntries:
      return (
        <OpenSourceCreditEntries
          key={newComponentKey}
          data={new OpenSourceCreditEntriesModel(component)}
        />
      );
    case Types.RedirectComponent:
      return (
        <RedirectComponent
          key={newComponentKey}
          data={new RedirectComponentModel(component)}
        />
      );
    case Types.Reviews:
      return (
        <Reviews key={newComponentKey} data={new ReviewsModel(component)} />
      );
    case Types.RichtextComponent:
      return (
        <Richtext key={newComponentKey} data={new RichtextModel(component)} />
      );
    case Types.SalesforceChat:
      return (
        <SalesforceChat
          key={newComponentKey}
          data={new SalesforceChatModel(component)}
        />
      );
    case Types.SectionComponent:
      return (
        <Section key={newComponentKey} data={new SectionModel(component)} />
      );
    case Types.Specifications:
      return (
        <Specifications
          key={newComponentKey}
          data={new SpecificationsModel(component)}
        />
      );
    case Types.Testimonials:
      return (
        <Testimonials
          key={newComponentKey}
          data={new TestimonialsModel(component)}
        />
      );
    case Types.TitleBanner:
      return (
        <TitleBanner
          key={newComponentKey}
          data={new TitleBannerModel(component)}
        />
      );
    case Types.TwoUp:
      return <TwoUp key={newComponentKey} data={new TwoUpModel(component)} />;
    case Types.VerticalSpacerComponent:
      return (
        <VerticalSpacer
          key={newComponentKey}
          data={new VerticalSpacerModel(component)}
        />
      );
    case Types.VideoTiles:
      return (
        <VideoTiles key={newComponentKey} {...new VideoTilesModel(component)} />
      );
    case Types.PdfDisplay:
      return (
        <PdfDisplay
          key={newComponentKey}
          data={new PdfDisplayModel(component)}
        />
      );
    default:
      return <></>;
  }
};

export default getComponent;
